// src/utils/date.js
// Normalize many input shapes into YYYY-MM-DD (local date) and ms.
// This module deliberately only yields camelCase fields (startDate/endDate).
export function toIsoDateOnly(input) {
    if (input == null) return null;

    // Firestore Timestamp (.toDate)
    if (typeof input?.toDate === "function") {
        try {
            const dt = input.toDate();
            if (isNaN(dt.getTime())) return null;
            return dt.toISOString().slice(0, 10);
        } catch (e) {
            return null;
        }
    }

    // Firestore-like { seconds, nanoseconds }
    if (typeof input === "object" && typeof input.seconds === "number") {
        const nanos = typeof input.nanoseconds === "number" ? Math.round(input.nanoseconds / 1e6) : 0;
        const ms = input.seconds * 1000 + nanos;
        const dt = new Date(ms);
        if (isNaN(dt.getTime())) return null;
        return dt.toISOString().slice(0, 10);
    }

    // number timestamp (ms)
    if (typeof input === "number" && Number.isFinite(input)) {
        const dt = new Date(input);
        if (isNaN(dt.getTime())) return null;
        return dt.toISOString().slice(0, 10);
    }

    // string already in YYYY-MM-DD
    const s = String(input).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

    // fallback parse (ISO e.g. "2025-10-09T..." or other)
    const parsed = Date.parse(s);
    if (Number.isFinite(parsed)) {
        return new Date(parsed).toISOString().slice(0, 10);
    }

    return null;
}

// Inclusive end-of-day ms for a date input (treat YYYY-MM-DD as local end-of-day)
export function toInclusiveMs(input) {
    const iso = toIsoDateOnly(input);
    if (!iso) {
        const parsed = Date.parse(String(input || ""));
        return Number.isFinite(parsed) ? parsed : NaN;
    }
    const [y, mo, d] = iso.split("-").map(Number);
    const dt = new Date(y, mo - 1, d, 23, 59, 59, 999); // local end of that day
    return Number.isFinite(dt.getTime()) ? dt.getTime() : NaN;
}

// Read-time normalizer for a trip object: returns a shallow copy that guarantees
// startDate and endDate are YYYY-MM-DD strings (or null). Does NOT create snake_case keys.
export function normalizeTripDatesCamel(trip = {}) {
    if (!trip || typeof trip !== "object") return { ...trip };
    const startRaw = trip.startDate ?? trip.start_date ?? trip.start ?? null;
    const endRaw = trip.endDate ?? trip.end_date ?? trip.end ?? null;

    const startIso = toIsoDateOnly(startRaw);
    const endIso = toIsoDateOnly(endRaw);

    return {
        ...trip,
        startDate: startIso ?? (trip.startDate ?? null),
        endDate: endIso ?? (trip.endDate ?? null),
    };
}
